/* eslint-disable no-param-reassign */
import axios, { AxiosInstance } from 'axios';
import { Auth } from 'aws-amplify';
import { BackendError, errorMessage } from '@services';

const configureInstance = (instance: AxiosInstance) => {
  instance.defaults.headers.common['Content-Type'] = 'application/json';

  instance.defaults.validateStatus = (status) => {
    if (status === 401 || status === 403) {
      Auth.signOut();
    }
    return status < 400;
  };

  instance.interceptors.response.use(undefined, (e) => {
    const error = e as BackendError;
    if (error.response?.data) {
      const { messages } = error.response.data;
      if (Array.isArray(messages)) {
        messages.forEach((msg) => errorMessage(msg));
      } else {
        errorMessage(messages);
      }
    }
    throw error;
  });

  instance.interceptors.request.use(
    (config) =>
      new Promise((resolve) => {
        Auth.currentSession()
          .then((session) => {
            const idTokenExpire = session.getIdToken().getExpiration();
            const refreshToken = session.getRefreshToken();
            const currentTimeSeconds = Math.round(+new Date() / 1000);
            const accessToken = `Bearer ${session.getAccessToken().getJwtToken()}`;

            if (idTokenExpire < currentTimeSeconds) {
              Auth.currentAuthenticatedUser().then((user) => {
                // @ts-expect-error-this-line
                user.refreshSession(refreshToken, (err) => {
                  if (err) {
                    Auth.signOut();
                  } else {
                    config.headers = {
                      ...config.headers,
                      Authorization: accessToken,
                    };
                    resolve(config);
                  }
                });
              });
            } else {
              config.headers = {
                ...config.headers,
                Authorization: accessToken,
              };
              resolve(config);
            }
          })
          .catch(() => {
            // No logged-in user
            resolve(config);
          });
      }),
  );
};

export const createHttpClient = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });
  configureInstance(instance);
  return instance;
};
